import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import WarningOutlinedIcon from '@mui/icons-material/WarningOutlined';
import LoadingButton from '@mui/lab/LoadingButton';
import { styled } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import type { TooltipProps } from '@mui/material/Tooltip';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useFetchers } from '@remix-run/react';
import * as React from 'react';
import { isFetching } from '~/utils/common';

export const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[1],
  },
}));

type PhoneDialogProps = {
  open: boolean;
  phone: string;
  onClose: () => void;
  onSave: () => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};
export function PhoneDialog({ open, phone, onClose, onSave, onChange }: PhoneDialogProps) {
  return (
    <Dialog open={open} sx={{ zIndex: '1399' }} PaperProps={{ sx: { maxWidth: 450, borderRadius: 3 } }}>
      <IconButton onClick={onClose} sx={{ position: 'absolute', top: 15, right: 15, p: 0 }}>
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <Stack spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: '#FAEDE0', mt: 1.5, width: 48, height: 48 }}>
            <WarningOutlinedIcon sx={{ color: '#F59126', fontSize: 32 }} />
          </Avatar>
          <Typography variant="h6" textAlign="center" color="#000000">
            Ups. It looks like your profile doesn't have a phone number assigned
          </Typography>
          <Typography variant="body2" textAlign="center" color="text.secondary">
            Please enter your phone number so we can ensure that we can contact you if needed. Please make sure to
            include the country code if you are outside the US.
          </Typography>
          <Grid container>
            <TextField
              name="phone"
              variant="outlined"
              label="Phone Number"
              size="small"
              value={phone}
              required
              fullWidth
              onChange={onChange}
            />
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onSave} variant="contained" size="large" disableElevation disabled={!phone}>
          SAVE PHONE
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type ConfirmationDialogProps = {
  open: boolean;
  onClose: () => void;
};
export function ConfirmationDialog({ open, onClose }: ConfirmationDialogProps) {
  const fetchers = useFetchers();
  const isSubmitting = isFetching(fetchers, ['saveDraft']);

  const Icon = isSubmitting ? HourglassEmptyOutlinedIcon : CheckIcon;
  const iconColor = isSubmitting ? '#F59126' : '#2A5D20';
  const avatarBgColor = isSubmitting ? '#FAEDE0' : '#E7EFE6';

  return (
    <Dialog open={open} sx={{ zIndex: '1399' }} PaperProps={{ sx: { maxWidth: 450, borderRadius: 3 } }}>
      <DialogContent>
        <Stack spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: avatarBgColor, mt: 1.5, width: 48, height: 48 }}>
            <Icon sx={{ color: iconColor, fontSize: 32 }} />
          </Avatar>
          <Typography variant="h6" textAlign="center" color="#000000">
            {isSubmitting
              ? 'Your draft order is being submitted.'
              : 'Your draft order has been submitted successfully!'}
          </Typography>
          <Typography variant="body2" textAlign="center" color="text.secondary">
            Your design is being processed. The SwagCharm team will contact you soon to manage the order.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center' }}>
        <LoadingButton
          onClick={onClose}
          variant="contained"
          size="large"
          disableElevation
          loading={isSubmitting}
          loadingPosition="start"
          sx={{ width: 260, '& .MuiLoadingButton-loadingIndicator': { left: 50 } }}
        >
          {isSubmitting ? 'SUBMITTING...' : 'DONE'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
